import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  TextField,
  Typography,
  Button,
  Container,
  Paper,
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  Alert,
  alpha,
  Divider
} from '@mui/material';
import {
  Email,
  Phone,
  Login as LoginIcon,
  PersonAdd,
  Send,
  VerifiedUser
} from '@mui/icons-material';
import '../assets/css/style.css';
import API_BASE_URL from '../config'; // Replace with your API base URL
import { useDispatch } from 'react-redux';
import { setIsLoggedIn, setUser } from '../store/authSlice';
import { useNavigate } from 'react-router-dom';

// Styled Components
const PageContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  padding: theme.spacing(4),
}));

const GuestPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: 450,
  borderRadius: theme.shape.borderRadius * 2,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  textAlign: 'center',
  marginBottom: theme.spacing(1),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 1.5,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.95),
      transform: 'translateY(-1px)',
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.background.paper, 1),
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 3,
  padding: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 5px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

const Guest = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [input, setInput] = useState('');
  const [otp, setOtp] = useState('');
  const [message, setMessage] = useState('');
  const [timer, setTimer] = useState(90);
  const [isOTPSent, setIsOTPSent] = useState(false);
 
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isUidLogin, setIsUidLogin] = useState(false);
  const [email_id, setEmail] = useState('');
  const [phone_number, setPhone] = useState('');
  const [uid, setUid] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    if (isOTPSent && timer > 0) {
      const countdown = setInterval(() => setTimer((prevTimer) => prevTimer - 1), 1000);
      return () => clearInterval(countdown);
    }
  }, [isOTPSent, timer]);

  const validateInput = (input) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phonePattern = /^\d{10}$/;
    return emailPattern.test(input) || phonePattern.test(input);
  };

  const isPhoneNumber = (value) => /^\d+$/.test(value);

  const fetchAndSetOTP = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/get-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ input: input }),
      });

      console.log('response--->',response);
      if (!response.ok) {
        throw new Error('Failed to fetch OTP');
      }

      const data = await response.json();
      setOtp(data.otp);
      return data.otp;
    } catch (error) {
      console.error('Error fetching OTP:', error);
      setError('Error fetching OTP');
      return null;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateInput(input)) {
      setError('Please enter a valid email or phone number');
      setLoading(false);
      return;
    }

    try {
      if (!isOTPSent) {
        const response = await fetch('/goldpetals/api/send-otp-guest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ input }),
        });

        const data = await response.json();
        console.log('data',data);
        if (response.ok) {
          setIsOTPSent(true);
          setTimer(90);
          const fetchedOTP = await fetchAndSetOTP();
          console.log('fetchedOTP',fetchedOTP);
          if (fetchedOTP) {
            setTimeout(() => {
              verifyOTP(fetchedOTP);
            }, 1000);
          }
        } else {
          setError(data.message || 'Failed to send OTP');
        }
      } else {
        await verifyOTP(otp);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = () => {
    setIsOTPSent(false);
    setTimer(90);
    handleLogin(new Event('submit'));
  };

  const verifyOTP = async (otp) => {
    try {
      const isPhone = /^\d{10}$/.test(input);
      const requestBody = isPhone 
        ? { phone_number: input, otp }
        : { email_id: input, otp };

      const response = await fetch(`${API_BASE_URL}/verify-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to verify OTP');
      }

      const data = await response.json();
      
      // Check if user is disabled
      if (data.status === 0) {
        setError('Your account is disabled. Please contact administrator.');
        return;
      }

      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('uid', data.uid);
      localStorage.setItem('userRole', data.role);
      console.log('data',data);
      // Redirect based on status
      if (data.status == 1) {
        navigate('/contact-us');
      } else {
        navigate('/home');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setError('Error verifying OTP');
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;

    // Allow only 10 digits for phone number input
    if (/^\d*$/.test(value) && value.length <= 10) {
      setInput(value);
    } else if (!/^\d*$/.test(value)) {
      // If the input is not a number (likely an email), update as is
      setInput(value);
    }
  };

  return (
    <PageContainer maxWidth={false}>
      <GuestPaper elevation={3}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Logo>GOLD PETALS</Logo>
          <Typography variant="h5" gutterBottom sx={{ 
            fontWeight: 700,
            background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}>
            Guest Login
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome! Please enter your details to continue
          </Typography>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              backgroundColor: alpha('#ff3333', 0.1)
            }}
          >
            {error}
          </Alert>
        )}

        <form onSubmit={handleLogin}>
          <Stack spacing={3}>
            <StyledTextField
              fullWidth
              label="Email or Phone Number"
              value={input}
              onChange={handleInputChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {isPhoneNumber(input) ? <Phone color="primary" /> : <Email color="primary" />}
                  </InputAdornment>
                ),
              }}
            />

            {isOTPSent && (
              <Box>
                <StyledTextField
                  fullWidth
                  label="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VerifiedUser color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ display: 'block', textAlign: 'center', mt: 1 }}
                >
                  {timer > 0 ? (
                    `Resend OTP in ${timer}s`
                  ) : (
                    <Button
                      onClick={handleResendOTP}
                      sx={{ textTransform: 'none' }}
                    >
                      Resend OTP
                    </Button>
                  )}
                </Typography>
              </Box>
            )}

            <ActionButton
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading || checkingStatus}
              startIcon={isOTPSent ? <LoginIcon /> : <Send />}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : checkingStatus ? (
                'Checking Status...'
              ) : isOTPSent ? (
                'Verify OTP'
              ) : (
                'Send OTP'
              )}
            </ActionButton>
          </Stack>
        </form>

        <Box sx={{ mt: 4 }}>
          <Divider>
            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
              OR
            </Typography>
          </Divider>
        </Box>

        <Stack spacing={2} sx={{ mt: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate('/signup')}
            startIcon={<PersonAdd />}
            sx={{ 
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Create New Account
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate('/login')}
            startIcon={<LoginIcon />}
            sx={{ 
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Login to Existing Account
          </Button>
        </Stack>
      </GuestPaper>
    </PageContainer>
  );
};

export default Guest;
